import { api } from '../../../services/axios';
import { authService } from '../../../services/auth';
import type { User, Setor, Processo, Demanda, CreateDemandaRequest } from '../../../models';

class ApiService {
  private cache = new Map<string, { data: unknown; timestamp: number }>();
  private inFlightRequests = new Map<string, Promise<unknown>>();
  private cacheTTL = 2 * 60 * 1000; // 2 minutos

  private getCachedValue<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) {
      return null;
    }

    if (Date.now() - entry.timestamp > this.cacheTTL) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  private setCachedValue<T>(key: string, value: T) {
    this.cache.set(key, { data: value, timestamp: Date.now() });
  }

  private getInFlightPromise<T>(key: string): Promise<T> | null {
    return (this.inFlightRequests.get(key) as Promise<T> | undefined) ?? null;
  }

  private setInFlightPromise<T>(key: string, promise: Promise<T>) {
    this.inFlightRequests.set(key, promise as Promise<unknown>);
  }

  private clearInFlightPromise(key: string) {
    this.inFlightRequests.delete(key);
  }

  private invalidateCache(keys: string | string[]) {
    const keyList = Array.isArray(keys) ? keys : [keys];
    keyList.forEach((key) => this.cache.delete(key));
  }

  private invalidateCacheByPrefix(prefixes: string | string[]) {
    const prefixList = Array.isArray(prefixes) ? prefixes : [prefixes];

    for (const key of this.cache.keys()) {
      if (prefixList.some((prefix) => key.startsWith(prefix))) {
        this.cache.delete(key);
      }
    }

    for (const key of this.inFlightRequests.keys()) {
      if (prefixList.some((prefix) => key.startsWith(prefix))) {
        this.inFlightRequests.delete(key);
      }
    }
  }

  private async fetchWithCache<T>(
    key: string,
    requestFn: () => Promise<T>,
    forceRefresh = false
  ): Promise<T> {
    if (!forceRefresh) {
      const cached = this.getCachedValue<T>(key);
      if (cached) {
        return cached;
      }

      const inFlight = this.getInFlightPromise<T>(key);
      if (inFlight) {
        return inFlight;
      }
    } else {
      this.invalidateCache(key);
    }

    const requestPromise = (async () => {
      const data = await requestFn();
      this.setCachedValue(key, data);
      return data;
    })();

    this.setInFlightPromise(key, requestPromise);

    try {
      return await requestPromise;
    } finally {
      this.clearInFlightPromise(key);
    }
  }

  // Usuários
  async getUsuarios(): Promise<User[]> {
    return this.fetchWithCache('usuarios', async () => {
      await authService.ensureSanctumInitialized();
      const response = await api.get<User[]>('/usuarios');
      return response.data;
    });
  }

  async getUsuarioAtual(forceRefresh = false): Promise<User> {
    return this.fetchWithCache(
      'usuarioAtual',
      async () => {
        await authService.ensureSanctumInitialized();
        return authService.getCurrentUser(forceRefresh);
      },
      forceRefresh
    );
  }

  async getUsuariosPorSetor(setorId: number): Promise<User[]> {
    await authService.ensureSanctumInitialized();
    const response = await api.get<User[]>(`/usuarios/setor/${setorId}`);
    return response.data;
  }

  async getUsuariosDosSetoresDoUsuarioLogado(): Promise<User[]> {
    await authService.ensureSanctumInitialized();
    
    // Buscar os setores do usuário logado
    const meusSetores = await this.getMeusSetores();
    
    // Se não tiver nenhum setor, retornar vazio
    if (!meusSetores || meusSetores.length === 0) {
      return [];
    }
    
    // Buscar usuários de cada setor e consolidar
    const usuariosMap = new Map<number, User>();
    
    await Promise.all(
      meusSetores.map(async (setor) => {
        try {
          const setorComUsuarios = await this.getSetorComUsuarios(setor.id);
          
          // Adicionar usuários ao mapa (evita duplicatas)
          if (setorComUsuarios.usuarios) {
            setorComUsuarios.usuarios.forEach((usuario: User) => {
              if (!usuariosMap.has(usuario.id)) {
                usuariosMap.set(usuario.id, usuario);
              }
            });
          }
        } catch (error) {
          console.error(`Erro ao buscar usuários do setor ${setor.id}:`, error);
        }
      })
    );
    
    // Retornar array de usuários únicos
    return Array.from(usuariosMap.values());
  }

  // Setores
  async getSetores(forceRefresh = false): Promise<Setor[]> {
    return this.fetchWithCache(
      'setores',
      async () => {
        await authService.ensureSanctumInitialized();
        const response = await api.get<Setor[]>('/setores');
        return response.data;
      },
      forceRefresh
    );
  }

  async getSetoresCompletos(filters?: { buscar?: string; equipe?: boolean }, forceRefresh = false): Promise<Setor[]> {
    const cacheKey = filters ? `setoresCompletos_${JSON.stringify(filters)}` : 'setoresCompletos';
    return this.fetchWithCache(
      cacheKey,
      async () => {
        await authService.ensureSanctumInitialized();
        const params = new URLSearchParams();
        if (filters?.buscar) params.append('buscar', filters.buscar);
        if (filters?.equipe !== undefined) params.append('equipe', String(filters.equipe));
        const queryString = params.toString() ? `?${params.toString()}` : '';
        const response = await api.get<Setor[]>(`/setores/completos${queryString}`);
        return response.data;
      },
      forceRefresh
    );
  }

  async getSetorComUsuarios(setorId: number): Promise<Setor> {
    await authService.ensureSanctumInitialized();
    const response = await api.get<Setor>(`/setores/${setorId}/usuarios`);
    return response.data;
  }

  // Setores do usuário logado
  async getMeusSetores(forceRefresh = false): Promise<Setor[]> {
    return this.fetchWithCache(
      'meusSetores',
      async () => {
        await authService.ensureSanctumInitialized();
        const response = await api.get<Setor[]>('/me/setores');
        return response.data;
      },
      forceRefresh
    );
  }

  async definirSetorLogado(setorId: number): Promise<{ message: string; setor_logado: Setor; user: User }> {
    await authService.ensureSanctumInitialized();
    const response = await api.put<{ message: string; setor_logado: Setor; user: User }>(`/me/setor-logado/${setorId}`);
    this.invalidateCache('usuarioAtual');
    return response.data;
  }

  // Processos para criar demanda
  async getProcessos(): Promise<Processo[]> {
    return this.fetchWithCache('processos', async () => {
      await authService.ensureSanctumInitialized();
      const response = await api.get<Processo[]>('/processos/filter');
      return response.data;
    });
  }
  
  // Processos completos
  async getProcessosList(filters?: { buscar?: string; setor?: number }): Promise<Processo[]> {
    const cacheKey = filters ? `processosList_${JSON.stringify(filters)}` : 'processosList';
    return this.fetchWithCache(cacheKey, async () => {
      await authService.ensureSanctumInitialized();
      const params = new URLSearchParams();
      if (filters?.buscar) params.append('buscar', filters.buscar);
      if (filters?.setor) params.append('setor', String(filters.setor));
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await api.get<Processo[]>(`/processos${queryString}`);
      return response.data;
    });
  }
  
  async getProcessosPorSetor(setorId: number): Promise<Processo[]> {
    await authService.ensureSanctumInitialized();
    const response = await api.get<Processo[]>(`/processos/setor/${setorId}`);
    return response.data;
  }

  // Obter dias úteis (SLA) por processo e prioridade
  async getProcessoDiasUteis(processoId: number): Promise<{
    baixa: string;
    media: string;
    alta: string;
    urgente: string;
  }> {
    await authService.ensureSanctumInitialized();
    const response = await api.post<{
      baixa: string;
      media: string;
      alta: string;
      urgente: string;
    }>(`/processos/${processoId}/dias`);
    return response.data;
  }

  // Processos CRUD
  async createProcesso(
    processo: { 
      nome_processo: string; 
      prioridade_baixa: number;
      prioridade_media: number;
      prioridade_alta: number;
      prioridade_urgente: number;
      setor_processo_fk: number; 
      descricao: string;
      instrucoes?: string;
      links?: Array<{link: string; titulo?: string}>;
      autorizacoes?: Array<{autorizador_fk: number, ordem: number | null}>;
    } | FormData
  ): Promise<Processo> {
    await authService.ensureSanctumInitialized();
    
    // Se for FormData, enviar direto
    if (processo instanceof FormData) {
      const response = await api.post<{ processo: Processo }>('/processos', processo, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      this.invalidateCache(['processos', 'processosList']);
      this.invalidateCacheByPrefix('processosList_');
      return response.data.processo;
    }
    
    // Caso contrário, enviar JSON normal
    const response = await api.post<{ processo: Processo }>('/processos', processo);
    this.invalidateCache(['processos', 'processosList']);
    this.invalidateCacheByPrefix('processosList_');
    return response.data.processo;
  }

  async updateProcesso(
    id: number, 
    processo: { 
      nome_processo: string; 
      prioridade_baixa: number;
      prioridade_media: number;
      prioridade_alta: number;
      prioridade_urgente: number;
      setor_processo_fk: number; 
      descricao: string;
      instrucoes?: string;
      links?: Array<{link: string; titulo?: string}>;
      autorizacoes?: Array<{autorizador_fk: number, ordem: number | null}>;
    } | FormData
  ): Promise<Processo> {
    await authService.ensureSanctumInitialized();
    
    // Se for FormData, usar POST com _method=PUT (Laravel FormData limitation)
    if (processo instanceof FormData) {
      processo.append('_method', 'PUT');
      const response = await api.post<{ processo: Processo }>(`/processos/${id}`, processo, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      this.invalidateCache(['processos', 'processosList']);
      this.invalidateCacheByPrefix('processosList_');
      return response.data.processo;
    }
    
    // Caso contrário, enviar JSON normal com PUT
    const response = await api.put<{ processo: Processo }>(`/processos/${id}`, processo);
    this.invalidateCache(['processos', 'processosList']);
    this.invalidateCacheByPrefix('processosList_');
    return response.data.processo;
  }

  async deleteProcesso(id: number): Promise<{ message: string }> {
    await authService.ensureSanctumInitialized();
    const response = await api.delete<{ message: string }>(`/processos/${id}`);
    this.invalidateCache(['processos', 'processosList']);
    this.invalidateCacheByPrefix('processosList_');
    return response.data;
  }

  // Setores CRUD
  async createSetor(setor: { 
    nome_setor: string; 
    chefe_setor_fk?: number; 
    equipe?: boolean;
    usuarios?: number[];
    cidade_fk: number;
  }): Promise<Setor> {
    await authService.ensureSanctumInitialized();
    const setorData = {
      ...setor,
      equipe: false // Marca explicitamente como setor (não equipe)
    };
    const response = await api.post<Setor>('/setores', setorData);
    this.invalidateCache(['setores', 'setoresCompletos', 'meusSetores']);
    this.invalidateCacheByPrefix('setoresCompletos_');
    return response.data;
  }

  async updateSetor(id: number, setor: { 
    nome_setor: string; 
    chefe_setor_fk?: number; 
    equipe?: boolean;
  }): Promise<Setor> {
    await authService.ensureSanctumInitialized();
    const response = await api.put<Setor>(`/setores/${id}`, setor);
    this.invalidateCache(['setores', 'setoresCompletos', 'meusSetores']);
    this.invalidateCacheByPrefix('setoresCompletos_');
    return response.data;
  }

  async deleteSetor(id: number): Promise<{ message: string }> {
    await authService.ensureSanctumInitialized();
    const response = await api.delete<{ message: string }>(`/setores/${id}`);
    this.invalidateCache(['setores', 'setoresCompletos', 'meusSetores']);
    this.invalidateCacheByPrefix('setoresCompletos_');
    return response.data;
  }

  // Equipes (usando o mesmo endpoint de setores com flag equipe: true)
  async createEquipe(equipe: { 
    nome_setor: string; 
    chefe_setor_fk?: number; 
    usuarios?: number[];
    cidade_fk: number;
  }): Promise<Setor> {
    await authService.ensureSanctumInitialized();
    const equipeData = {
      ...equipe,
      equipe: true // Marca explicitamente como equipe
    };
    const response = await api.post<Setor>('/setores', equipeData);
    this.invalidateCache(['setores', 'setoresCompletos', 'meusSetores']);
    this.invalidateCacheByPrefix('setoresCompletos_');
    return response.data;
  }

  // Vincular/desvincular usuários
  async vincularUsuariosSetor(setorId: number, usuariosIds: number[]): Promise<{ message: string }> {
    await authService.ensureSanctumInitialized();
    const response = await api.post<{ message: string }>(`/usuarios/${setorId}/setores/vincular`, {
      usuarios_ids: usuariosIds
    });
    this.invalidateCache(['setores', 'setoresCompletos', 'meusSetores']);
    return response.data;
  }

  async desvincularUsuarioSetor(usuarioId: number, setorId: number): Promise<{ message: string }> {
    await authService.ensureSanctumInitialized();
    const response = await api.delete<{ message: string }>(`/usuarios/${usuarioId}/setores/${setorId}`);
    this.invalidateCache(['setores', 'setoresCompletos', 'meusSetores']);
    return response.data;
  }

  // Demandas
  async getDemandas(): Promise<Demanda[]> {
    await authService.ensureSanctumInitialized();
    const response = await api.get<Demanda[]>('/demandas');
    return response.data;
  }

  async createDemanda(demanda: CreateDemandaRequest, anexos?: File[]): Promise<Demanda> {
    await authService.ensureSanctumInitialized();
    
    // Se não há anexos, usar o método JSON normal
    if (!anexos || anexos.length === 0) {
      const response = await api.post<{ demanda: Demanda }>('/demandas', demanda);
      return response.data.demanda;
    }

    // Se há anexos, usar FormData
    const formData = new FormData();
    
    // Adicionar os dados da demanda
    Object.entries(demanda).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Tratar arrays especiais (observadores e processo_fk)
        if ((key === 'observadores' || key === 'processo_fk') && Array.isArray(value)) {
          value.forEach((item, index) => {
            formData.append(`${key}[${index}]`, item.toString());
          });
          return;
        }
        
        // Tratar template_respostas como JSON
        if (key === 'template_respostas' && typeof value === 'object') {
          formData.append(key, JSON.stringify(value));
          return;
        }
        
        formData.append(key, value.toString());
      }
    });
    
    // Adicionar os anexos
    anexos.forEach((file) => {
      formData.append('anexos[]', file);
    });

    const response = await api.post<{ demanda: Demanda }>('/demandas', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data.demanda;
  }

  async createDemandaCustomizada(demandas: Array<{
    processo_fk: number;
    demandante_fk: number;
    titulo: string;
    prioridade: string;
    descricao: string;
    sugestao_entrega: string;
    observadores: number[];
    anexos: File[];
  }>): Promise<any> {
    await authService.ensureSanctumInitialized();
    
    const formData = new FormData();
    
    // Processar cada demanda
    demandas.forEach((demanda, index) => {
      // Adicionar dados da demanda
      formData.append(`demandas[${index}][processo_fk]`, demanda.processo_fk.toString());
      formData.append(`demandas[${index}][demandante_fk]`, demanda.demandante_fk.toString());
      formData.append(`demandas[${index}][titulo]`, demanda.titulo);
      formData.append(`demandas[${index}][prioridade]`, demanda.prioridade);
      formData.append(`demandas[${index}][descricao]`, demanda.descricao);
      formData.append(`demandas[${index}][sugestao_entrega]`, demanda.sugestao_entrega);
      
      // Adicionar observadores
      demanda.observadores.forEach((obsId, obsIndex) => {
        formData.append(`demandas[${index}][observadores][${obsIndex}]`, obsId.toString());
      });
      
      // Adicionar anexos específicos
      demanda.anexos.forEach((file, fileIndex) => {
        formData.append(`demandas[${index}][anexos][${fileIndex}]`, file);
      });
    });
    
    const response = await api.post<any>('/demandas', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  }

  async updateDemanda(id: number, demanda: Partial<CreateDemandaRequest>): Promise<Demanda> {
    await authService.ensureSanctumInitialized();
    const response = await api.put<{ demanda: Demanda }>(`/demandas/${id}`, demanda);
    return response.data.demanda;
  }

  async deleteDemanda(id: number): Promise<{ message: string }> {
    await authService.ensureSanctumInitialized();
    const response = await api.delete<{ message: string }>(`/demandas/${id}`);
    return response.data;
  }

  async atualizarFuncaoUsuario(setorId: number, usuarioId: number, funcao: 'USUARIO' | 'GESTOR'): Promise<{ message: string }> {
    await authService.ensureSanctumInitialized();
    const response = await api.put<{ message: string }>(`/setores/${setorId}/usuarios/${usuarioId}/funcao`, {
      funcao
    });
    // Invalidar cache de setores quando uma função for alterada
    this.invalidateCache(['setores', `setor-${setorId}`]);
    return response.data;
  }
}

export const apiService = new ApiService();
